import React, { useEffect, useRef, useState } from 'react';
import { signInWithCustomToken, signOut, updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase';
import { isAllowedDomain } from '../utils/authDomain';
import { LoadingDots } from './ui';

// Backend cold-start budget. Heroku free dynos can take ~30s to wake.
const OAUTH_FETCH_TIMEOUT_MS = 45_000;
const OAUTH_COLD_START_HINT_MS = 6_000;
const REDEEMED_TOKEN_KEY = 'cg.oauth.redeemed_token_id';

interface OAuthCallbackHandlerProps {
  onError?: (message: string) => void;
}

/**
 * Single-instance OAuth callback handler. Mounted once at the App root so
 * the `?oauth_token=…` redemption runs exactly once regardless of which
 * route the user lands on, eliminating the per-`<Auth>`-instance dedup
 * concerns.
 */
const OAuthCallbackHandler: React.FC<OAuthCallbackHandlerProps> = ({ onError }) => {
  const [processing, setProcessing] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).has('oauth_token');
  });
  const [slow, setSlow] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get('oauth_token');
    if (!oauthToken) return;

    // Guard against double-redemption across StrictMode double-mounts AND
    // hard reloads of the OAuth callback URL.
    let alreadyRedeemed: string | null = null;
    try {
      alreadyRedeemed = window.sessionStorage.getItem(REDEEMED_TOKEN_KEY);
    } catch {
      // sessionStorage may be unavailable (private mode) — fall through.
    }
    if (alreadyRedeemed === oauthToken) {
      window.history.replaceState({}, '', window.location.pathname);
      setProcessing(false);
      return;
    }

    startedRef.current = true;
    try {
      window.sessionStorage.setItem(REDEEMED_TOKEN_KEY, oauthToken);
    } catch {
      // ignore — dedup is best-effort.
    }

    // Strip the token from the URL immediately so a reload (or StrictMode
    // re-run) can't redeem it twice.
    window.history.replaceState({}, '', window.location.pathname);

    void redeem(oauthToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show a "still working" hint after a few seconds during OAuth so the user
  // knows the cold-start is expected, not a hang.
  useEffect(() => {
    if (!processing) {
      setSlow(false);
      return;
    }
    const t = setTimeout(() => setSlow(true), OAUTH_COLD_START_HINT_MS);
    return () => clearTimeout(t);
  }, [processing]);

  const redeem = async (tokenId: string) => {
    setProcessing(true);
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), OAUTH_FETCH_TIMEOUT_MS);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(
        `${backendUrl}/auth/google/fetch-tokens?token_id=${encodeURIComponent(tokenId)}`,
        { signal: controller.signal },
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch tokens: ${errorText}`);
      }
      const data = await response.json();
      if (!data.success || !data.customToken || !data.tokens) {
        throw new Error('Invalid response from authentication server');
      }
      const result = await signInWithCustomToken(auth, data.customToken);
      const idTokenResult = await result.user.getIdTokenResult();
      const email = idTokenResult.claims.email as string;
      const displayName = idTokenResult.claims.name as string | undefined;
      const photoURL = idTokenResult.claims.picture as string | undefined;
      if (!email || !isAllowedDomain(email)) {
        await signOut(auth);
        throw new Error(
          'Only Culture Genesis and Culture Genesis Partnerships email addresses are allowed.',
        );
      }
      if (displayName || photoURL) {
        await updateProfile(result.user, {
          displayName: displayName || null,
          photoURL: photoURL || null,
        });
      }
      const { storeGoogleTokens } = await import('../services/api');
      await storeGoogleTokens(data.tokens);
      window.dispatchEvent(new CustomEvent('google-workspace-connected'));
      // Keep the splash up until App.tsx's onAuthStateChanged has observed the
      // new user. Otherwise we briefly flash the login card between
      // setProcessing(false) and the listener firing — which made users think
      // sign-in failed and click "Sign in with Google" a second time.
      await new Promise<void>((resolve) => {
        let unsub: (() => void) | null = null;
        const timer = setTimeout(() => {
          unsub?.();
          resolve();
        }, 3000);
        unsub = auth.onAuthStateChanged((u) => {
          if (!u) return;
          clearTimeout(timer);
          unsub?.();
          resolve();
        });
      });
    } catch (error) {
      console.error('[OAuthCallback] Error:', error);
      const message =
        (error as any)?.name === 'AbortError'
          ? 'The authentication server took too long to respond. Please try signing in again.'
          : (error as Error).message || 'Authentication failed. Please try again.';
      onError?.(message);
      // On failure, drop the redeemed-token guard so the user can retry from
      // a fresh OAuth flow.
      try {
        window.sessionStorage.removeItem(REDEEMED_TOKEN_KEY);
      } catch {
        // ignore
      }
    } finally {
      clearTimeout(timeoutHandle);
      setProcessing(false);
    }
  };

  if (!processing) return null;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img
          src="/assets/logo.svg"
          width={48}
          height={48}
          alt="Culture Genesis"
          style={{ marginBottom: 18, borderRadius: 0, maxWidth: 'unset', maxHeight: 'unset' }}
        />
        <LoadingDots />
        <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 12 }}>Signing you in…</div>
        {slow && (
          <div style={{ fontSize: 12, color: 'var(--fg-4)', marginTop: 6, maxWidth: 260, lineHeight: 1.5 }}>
            First sign-in of the day can take up to 30 seconds while the server warms up.
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'var(--cg-gradient-bg)',
    padding: 24,
    boxSizing: 'border-box',
    zIndex: 5000,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: 'rgba(255,255,255,0.78)',
    backdropFilter: 'blur(24px) saturate(150%)',
    WebkitBackdropFilter: 'blur(24px) saturate(150%)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-2xl)',
    padding: '36px 32px',
    boxShadow: 'var(--shadow-xl)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    fontFamily: 'var(--font-sans)',
  },
};

export default OAuthCallbackHandler;
